import { take, finalize, tap, switchMap } from 'rxjs/operators';
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';

import { AppService } from '../../../shared/services/app.service';
import { OfferService } from '../../services/offer.service';
import { FlashService } from '../../../shared/components/flash/services/flash.service';
import { OfferMixed } from '../../../shared/models/offer-mixed';
import { HIGHLIGHT_LEVELS } from '../../../shared/consts/highlights';
import { ProgramService } from '../../../programs/services/program.service';
import { Program } from '../../../shared/models/program';
import { dayDiff } from '../../../shared/utils/dates';
import { Offer } from 'app/shared/models/offer';
import { parseUniverseList } from 'app/shared/utils/universes';
import { MatDialog } from '@angular/material/dialog';
import { DialogConfirmComponent } from 'app/shared/components/dialog-confirm/dialog-confirm.component';
import { AuthService } from 'app/auth/services/auth.service';
import { Strategy } from '../../../shared/models/strategy';
import { StrategyService } from '../../../strategies/services/strategy.service';
import { FileService } from '~common/app/shared/services/file.service'
import {
  DecoratedUploaderComponent
} from '~common/app/shared/components/multi-images-selectable-list/decorated-uploader/decorated-uploader.component';
import { IllustrationUsage } from '~common/app/shared/components/multi-images-selectable-list/interfaces';
import { settings } from "~common/app/app.settings";
import { of } from 'rxjs';

@Component({
  selector: 'app-offer-preview-page',
  templateUrl: './offer-preview-page.component.html',
  styleUrls: ['./offer-preview-page.component.sass']
})
export class OfferPreviewPageComponent implements OnInit {
  @ViewChild('form') formComponent: NgForm;
  @ViewChild('decoratedUploaderComponent') decoratedUploaderComponent: DecoratedUploaderComponent<IllustrationUsage>;
  highlight_levels = HIGHLIGHT_LEVELS;
  offer: OfferMixed = new OfferMixed;
  offerIsNotEditable = false;
  program: Program = new Program;
  strategy: Strategy;
  loading: boolean;
  params: any;
  formSuccess = false;
  formError = false;
  isOfferTermsOpen = false;
  submitted = false;
  isActivationToggled = null;
  hasSegments = false;
  transactionSource = '';
  selectedFile: any = {};
  voucherPrices = [];
  customPicture = null;

  summary = {
    remaining_publication_days: 0,
    remaining_publication_days_percent: 0,
    remaining_start_days: 0,
    remaining_start_days_percent: 0,
    remaining_days: 0,
    remaining_days_percent: 0,
  };
  SSOEnabled: boolean;


  constructor(
    private appService: AppService,
    private fileService: FileService,
    private flashService: FlashService,
    private offerService: OfferService,
    private programService: ProgramService,
    private strategyService: StrategyService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    public dialog: MatDialog,
    private changeDetector: ChangeDetectorRef
  ) {
    this.appService.setTitle('offers.preview.heading');
    this.params = this.route.snapshot.paramMap;
  }

  ngOnInit() {
    this.SSOEnabled = settings.app.SSOEnabled;
    const id = this.params.get('id');

    this.loading = true;

    this.offerService.get(id).pipe(
      take(1),
      finalize(() => {
        this.loading = false;
      }),)
      .subscribe((response: any) => {
        this.offer = response;
        this.offerIsNotEditable = this.isNotEditable();
        this.isActivationToggled = this.offer.is_activated;
        if (this.offer.type === 'CASHBACK' && this.offerIsNotEditable === false) {
          this.fetchStrategy(this.offer).then(strategy => {
            this.strategy = strategy;
            if (this.strategy) {
              const currentPicture = Strategy.allStrategyPictures(this.strategy).find(
                picture => picture?.picture?.id === this.offer?.picture?.id);
              if (currentPicture) {
                this.offer.picture = currentPicture.picture;
              } else {
                this.customPicture = this.offer.picture;
                this.changeDetector.markForCheck();
              }
            }
          });
        }
      });

    this.programService.get().pipe(
      take(1))
      .subscribe(response => {
        this.program = response;
      });

    this.authService.getAuthenticatedUser().subscribe((response: any) => {
      if (response) {
        this.hasSegments = response.program?.configuration?.has_segments;
        this.transactionSource = response.program?.configuration?.main_source_transaction;
      }
    });


  }

  async fetchStrategy(offer: OfferMixed): Promise<Strategy> {
    if (!offer.strategy_id) {
      return null;
    }
    return await this.strategyService.get(this.offer.strategy_id).pipe(take(1)).toPromise();
  }

  isNotEditable() {
    return ['ENDED', 'COMPLETED', 'DEACTIVATED'].includes(this.offer.status);
  }

  isActiveToggleDisplayed() {
    return this.offer.status !== 'ENDED' && this.offer.status !== 'COMPLETED';
  }

  recipientHeadingName() {
    return this.offerService.computeRecipientName(this.offer);
  }

  getUniverseList(offer: Offer) {
    if (!offer || !offer.brand) {
      return '';
    }

    return parseUniverseList(offer.brand.universes);
  }

  onDownload(url, offer) {
    let downloadUrl = url;

    if (this.SSOEnabled) {
      downloadUrl = settings.api.url + url;
    }
    this.fileService.download(downloadUrl, offer.reference + '.csv');
  }

  onFileSelected(file) {
    this.selectedFile = file;
  }

  onFileUploaded(response) {
    this.offer.picture = response;
    this.flashService.success('shared.form.success.upload');
  }


  onCustomFileUploaded(media) {
    this.customPicture = media;
    this.offer.picture = this.customPicture;
    this.flashService.success('shared.form.success.upload');
  }

  onFileRemoved() {
    this.dialog.open(DialogConfirmComponent, {
      width: '400px',
      data: {
        text: 'shared.dialog.confirm.text.delete.picture'
      }
    }).afterClosed().subscribe(confirm => {
      if (confirm) {
        this.offer.picture = null;
      }
    });
  }

  onActivationChange() {
    if (this.offer.type === 'CASHBACK' && this.isActivationToggled === false) {
      this.dialog.open(DialogConfirmComponent, {
        width: '400px',
        data: {
          text: 'offers.preview.dialog.deactivate.text'
        }
      }).afterClosed().subscribe(confirm => {
        if (!confirm) {
          this.isActivationToggled = true;
        }
      });
    }
  }

  isActivationDisabled() {
    return !this.offer.is_activated && this.offer.type === 'CASHBACK';
  }

  onSave(formComponent) {
    const id = this.offer.id;
    this.formSuccess = false;
    this.formError = false;
    this.submitted = true;

    if (formComponent.form.valid) {
      this.loading = true;

      // Save first
      this.offerService.save(this.offer).pipe(
        switchMap((saveResponse) => {
          console.log('Save response:', saveResponse);

          // Then handle activation/deactivation if needed
          if (this.isActivationToggled !== this.offer.is_activated) {
            if (this.isActivationToggled === true) {
              return this.offerService.activateOffer(id).pipe(
                tap((response: any) => console.log('activateOffer', response))
              );
            } else {
              return this.offerService.deactivateOffer(id).pipe(
                tap((response: any) => console.log('deactivateOffer', response))
              );
            }
          }

          // No activation change needed
          return of(null);
        }),
        take(1),
        finalize(() => {
          setTimeout(() => {
            this.loading = false;
          }, 200);
        })
      ).subscribe({
        next: () => {
          this.formSuccess = true;
          this.flashService.success('offers.preview.form.success');
          this.onBack();
        },
        error: (error) => {
          console.error('Error:', error);
          this.formError = true;
        }
      });
    } else {
      this.formError = true;
    }
  }

  getAudienceState(): 'future' | 'computing' | 'ready' {
    if (new Date(this.offer.start_date) > new Date()) {
      return 'future';
    }
    if (!this.offer.audience?.computed_at) {
      return 'computing';
    }
    return 'ready';
  }

  computeAudienceFilename() {
    const date = new Date().toISOString().substring(19, 0).replace(/[Z:/-]/g, '').replace('T', '-');
    return this.offer.reference + '-' + date + '.csv';
  }

  getRemainingDays() {
    if (new Date(this.offer.start_date) > new Date()) {
      return dayDiff(this.offer.start_date, this.offer.end_date);
    }
    if (new Date(this.offer.end_date) < new Date()) {
      return 0;
    }
    return dayDiff(this.offer.start_date, new Date());
  }

  getRemainingDaysPercent() {
    const total = dayDiff(this.offer.start_date, this.offer.end_date);
    const remaining = this.getRemainingDays();
    return remaining / total * 100;
  }

  getRemainingPublicationDays() {
    if (new Date(this.offer.publication_date) > new Date()) {
      return dayDiff(new Date(), this.offer.publication_date);
    }
    return 0;
  }

  getRemainingPublicationDaysPercent() {
    const total = dayDiff(this.offer.publication_date, this.offer.created_at);
    const remaining = this.getRemainingPublicationDays();
    return 100 - (remaining / total * 100);
  }

  getRemainingStartDays() {
    if (new Date(this.offer.publication_date) > new Date()) {
      return dayDiff(this.offer.publication_date, this.offer.start_date);
    }
    if (new Date(this.offer.end_date) < new Date()) {
      return 0;
    }
    return dayDiff(this.offer.publication_date, new Date());
  }

  getRemainingStartDaysPercent() {
    const total = dayDiff(this.offer.publication_date, this.offer.start_date);
    const remaining = this.getRemainingStartDays();
    return 100 - (remaining / total * 100);
  }

  onBack() {
    if (window.history.length > 1) {
      this.location.back();
    } else {
      this.router.navigate(['/offers']);
    }
  }
}
